import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { Service } from '../../service.model';
import { ServiceProviderType } from '../../service-provider-type.enum';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { MyServicesService } from '../my-services.service';
import { switchMap, map } from 'rxjs/operators';
import { AngularFireStorage } from '@angular/fire/storage';
import * as uuid from 'uuid';
import { UploadTaskSnapshot } from '@angular/fire/storage/interfaces';

export class ServiceForm {
  public formGroup: FormGroup;
  public documentKey: string;
  public userId: string;
  public backupId: any;

  private myServicesService: MyServicesService;
  private firebaseStorage: AngularFireStorage;
  private providerTypeChanges$: BehaviorSubject<ServiceProviderType>;
  private profileUploaded$: BehaviorSubject<string>;
  public profileURL$: Observable<string>;
  public serviceProviderTags$: Observable<string[]>;
  public uploadingFile$ = new BehaviorSubject<boolean>(false);

  constructor(
    documentKey: string,
    service: Service,
    myServicesService: MyServicesService,
    firebaseStorage: AngularFireStorage
  ) {
    this.myServicesService = myServicesService;

    this.firebaseStorage = firebaseStorage;

    this.documentKey = documentKey;
    this.userId = service.userId;
    this.profileUploaded$ = new BehaviorSubject<string>(service.imageId);
    this.initializeForm();
    this.updateForm(service);
    this.setupProfileUrl();
  }

  private setupProfileUrl() {
    this.profileURL$ = this.profileUploaded$.asObservable().pipe(
      map(imageId => {
        if (!!imageId) {
          return `service-provider-profiles/${imageId}`;
        } else {
          return `unknown-profile.jpg`;
        }
      }),
      switchMap((url: string) => {
        try {
          return this.firebaseStorage.ref(url).getDownloadURL();
        } catch (error) {
          return this.firebaseStorage
            .ref(`unknown-profile.jpg`)
            .getDownloadURL();
        }
      })
    );
  }

  get hasDocumentKey(): boolean {
    return !!this.documentKey;
  }

  get model(): Service {
    const model = new Service();
    if (this.formGroup.value?.title) {
      model.title = this.formGroup.value?.title;
    }
    if (this.formGroup.value?.description) {
      model.description = this.formGroup.value?.description;
    }
    model.type = this.formGroup.value?.type;
    model.tags = this.formGroup.value?.tags ?? [];
    model.userId = this.userId;
    model.imageId = this.profileUploaded$.getValue() ?? null;
    model.locations = this.formGroup.value.locations;
    return model;
  }

  get validators() {
    return {
      title: [
        {
          type: 'required',
          message: 'Name is required.'
        }
      ],
      description: [
        {
          type: 'required',
          message: 'Description is required.'
        }
      ],
      type: [
        {
          type: 'required',
          message: 'Service Category is required.'
        }
      ],
      locations: [
        {
          type: 'required',
          message: 'At least one Location is required.'
        }
      ]
    };
  }

  get canDelete(): boolean {
    return !this.documentKey;
  }

  public updateForm(service: any) {
    if (!!service) {
      this.formGroup.patchValue({
        description: service.description,
        title: service.title,
        type: service.type,
        tags: service.tags
      });
      for (const location of service.locations ?? []) {
        (this.formGroup.controls.locations as FormArray).push(
          new FormControl(location)
        );
      }
    }
  }

  public uploadFile(file): string {
    const imageId = this.profileUploaded$.getValue() ?? uuid.v4();
    const filePath = `service-provider-profiles/${imageId}`;
    const ref = this.firebaseStorage.ref(filePath);
    const task = ref.put(file);
    this.uploadingFile$.next(true);
    from(task).subscribe(result => this.fileUploaded(result));
    return imageId;
  }

  public fileUploaded(result: UploadTaskSnapshot) {
    const imageId = result.ref.name;
    this.profileUploaded$.next(imageId);
    this.uploadingFile$.next(false);
  }

  private initializeForm() {
    this.formGroup = new FormGroup({
      title: new FormControl('', [Validators.required]),
      type: new FormControl(0, [Validators.required]),
      description: new FormControl('', [Validators.required]),
      tags: new FormControl({ value: [], disabled: true }),
      locations: new FormArray(
        [],
        [Validators.required, Validators.minLength(1)]
      )
    });

    this.providerTypeChanges$ = new BehaviorSubject<ServiceProviderType>(
      this.model.type
    );

    this.serviceProviderTags$ = this.providerTypeChanges$
      .asObservable()
      .pipe(switchMap(type => this.myServicesService.getServiceTypeTags(type)));

    this.formGroup.controls.type.valueChanges.subscribe(type =>
      this.typeChanged(type)
    );
  }

  private typeChanged(type: ServiceProviderType) {
    if (!!type || type === 0) {
      this.formGroup.patchValue({ tags: [] });
      this.providerTypeChanges$.next(type);
      this.formGroup.controls.tags.enable();
    } else {
      this.formGroup.controls.tags.disable();
    }
  }
}
